import {
  Children,
  isValidElement,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api.ts';
import { useShell } from '../shell/slots.tsx';

function cx(...classes: (string | false | null | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

export function Button({
  variant = 'primary',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' }) {
  // Botão com texto: o acabamento escolhe onde fica o ícone (ou se ele aparece). Só ícone, fica.
  const hasText = Children.toArray(props.children).some(
    (c) => typeof c === 'string' || typeof c === 'number',
  );
  return (
    <button
      type="button"
      {...props}
      data-has-text={hasText || undefined}
      className={cx(
        'ui-control ui-btn inline-flex items-center justify-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50',
        variant === 'primary' && 'ui-btn-primary shadow-sm hover:opacity-90',
        variant === 'secondary' && 'ui-button-secondary text-ink',
        variant === 'danger' && 'border border-danger text-danger hover:bg-danger hover:text-sheet',
        className,
      )}
    />
  );
}

export function PageHeader({
  title,
  description,
  meta,
  actions,
}: {
  title: string;
  description?: string;
  /**
   * Ficha em tela completa: a linha logo abaixo do título com o código (em fonte de código) e a
   * situação em `Badge` (ex.: "FAC-2026-000012 · Em execução").
   */
  meta?: ReactNode;
  actions?: ReactNode;
}) {
  // O cabeçalho no jeito do layout (ADR 0133): simples, numa faixa branca ou compacto (sem o texto
  // de apoio). Fora da moldura (prévia, testes), o simples.
  const header = useShell()?.options.header ?? 'simple';
  return (
    <div
      className={cx(
        'ui-page-header flex flex-wrap items-end justify-between gap-4 pb-4',
        header === 'band' && 'ui-page-band',
        header === 'compact' && 'pb-3',
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <h1 className="ui-page-title leading-tight tracking-tight">{title}</h1>
        {description && header !== 'compact' && (
          <p className="max-w-2xl text-sm text-muted">{description}</p>
        )}
        {meta && <div className="flex flex-wrap items-center gap-2 pt-1 text-sm">{meta}</div>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Indicador dos painéis: o número grande, com algarismos de mesma largura, e embaixo o que ele
 * conta. `alert` destaca em vermelho quando há algo a resolver (atrasados, risco); o rótulo diz o
 * quê, a cor só reforça.
 */
export function StatCard({
  label,
  value,
  alert = false,
  detail,
}: {
  label: string;
  /** Contagem (formatada aqui) ou valor já formatado por quem chama (ex.: um total em moeda). */
  value: number | string;
  alert?: boolean;
  /** Linha pequena embaixo do rótulo (ex.: a variação em relação a outra versão). */
  detail?: ReactNode;
}) {
  const { i18n } = useTranslation();
  const warn = alert && typeof value === 'number' && value > 0;
  return (
    <div
      className={cx(
        'flex flex-col gap-0.5 rounded-lg bg-sheet p-4',
        warn ? 'border border-danger shadow-sm' : 'ui-panel',
      )}
    >
      <span className={cx('text-2xl font-semibold tabular-nums', warn && 'text-danger')}>
        {typeof value === 'number' ? value.toLocaleString(i18n.language) : value}
      </span>
      <span className="text-sm text-muted">{label}</span>
      {detail && <span className="text-xs text-muted">{detail}</span>}
    </div>
  );
}

export function Panel({
  title,
  action,
  children,
}: {
  title?: string;
  /** Ação do bloco, à direita do título ("Anexar documento", "Nova obrigação"). */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="ui-panel flex flex-col gap-4 rounded-lg bg-sheet p-5">
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          {title && <h2 className="ui-head text-base">{title}</h2>}
          {action && <div className="ml-auto flex flex-wrap gap-2">{action}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: ReactNode;
  hint?: string;
  error?: string;
  /** Marca o campo como obrigatório (asterisco; leitores de tela ouvem "obrigatório"). */
  required?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <label className={cx('ui-field flex min-w-0 flex-col gap-1 text-sm', className)}>
      <span className="font-medium">
        {label}
        {required && (
          <>
            <span aria-hidden className="ml-0.5 text-danger">
              *
            </span>
            <span className="sr-only"> ({t('common.required')})</span>
          </>
        )}
      </span>
      {children}
      {hint && !error && <span className="text-xs text-muted">{hint}</span>}
      {error && <span className="text-xs text-danger">{error}</span>}
    </label>
  );
}

const inputClass =
  'ui-control ui-input w-full rounded-md border border-control-border bg-sheet px-3 py-1.5 text-sm text-ink focus:border-accent focus:ring-3 focus:ring-accent/20 focus:outline-none focus-visible:outline-none';

export function Input(props: ComponentProps<'input'>) {
  return <input {...props} className={cx(inputClass, props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(inputClass, 'ui-select', props.className)} />;
}

/** A cor de cada situação (a etiqueta desenha com ela pelo acabamento: suave, cheia, contorno, ponto). */
const BADGE_COLORS = {
  neutral: 'var(--muted)',
  ok: 'var(--ok)',
  // O dourado do tema é claro demais para texto: escurecido com a cor do texto.
  warn: 'color-mix(in srgb, var(--gold) 55%, var(--ink))',
  off: 'var(--danger)',
  info: 'var(--info)',
  accent: 'var(--brand-text)',
} as const;

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: keyof typeof BADGE_COLORS;
  children: ReactNode;
}) {
  return (
    <span
      className="ui-badge rounded-full py-0.5 text-xs font-medium whitespace-nowrap"
      style={{ '--c': BADGE_COLORS[tone] } as CSSProperties}
    >
      {children}
    </span>
  );
}

/** Foto ou iniciais de uma pessoa; redonda ou quadrada pelo acabamento. */
export function Avatar({ name, src, size = 32 }: { name: string; src?: string; size?: number }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return src ? (
    <img
      src={src}
      alt={name}
      className="ui-avatar object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      role="img"
      aria-label={name}
      className="ui-avatar inline-flex shrink-0 items-center justify-center bg-brand-soft text-xs font-semibold text-brand-text"
      style={{ width: size, height: size }}
    >
      {initials}
    </span>
  );
}

/** Título de coluna: texto ou `{ label, align: 'right' }` para valores e quantidades. */
export type TableHead = ReactNode | { label: ReactNode; align: 'right' };

function isAlignedHead(h: TableHead): h is { label: ReactNode; align: 'right' } {
  return typeof h === 'object' && h !== null && !isValidElement(h) && 'label' in h;
}

export function Table({ head, children }: { head: TableHead[]; children: ReactNode }) {
  // Na Planilha, os números de linha à esquerda (ajuste `rowNumbers`), por contador do CSS.
  const shell = useShell();
  const rowNumbers = shell?.shell === 'sheet' && shell.options.rowNumbers === 'show';
  return (
    <div className="ui-panel overflow-x-auto rounded-lg bg-sheet">
      <table
        data-row-numbers={rowNumbers || undefined}
        className="ui-table w-full min-w-[640px] border-collapse text-sm"
      >
        <thead className="ui-thead">
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                className={cx(
                  'border-b border-rule bg-sheet px-4 py-2.5 text-xs font-medium text-muted',
                  isAlignedHead(h) ? 'text-right' : 'text-left',
                )}
              >
                {isAlignedHead(h) ? h.label : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_td]:border-b [&_td]:border-rule [&_td]:px-4 [&_td]:py-3 [&_td]:align-middle [&_tr:hover_td]:bg-paper/60 [&_tr:last-child_td]:border-0">
          {children}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Tabela de valores por mês (Budget, Realizado, comparações): o cabeçalho e a primeira coluna
 * ficam fixos enquanto a tabela rola dentro da própria caixa, com linhas compactas. Linhas de
 * total e subtotal em `TotalRow`; valores em `NumCell`.
 */
export function ValueTable({
  head,
  label,
  children,
}: {
  head: TableHead[];
  /** Nome da tabela para leitores de tela (a caixa rola e recebe o foco do teclado). */
  label: string;
  children: ReactNode;
}) {
  return (
    <div
      role="region"
      aria-label={label}
      tabIndex={0}
      className="ui-panel max-h-[70vh] overflow-auto rounded-lg bg-sheet"
    >
      <table className="ui-table w-full min-w-max border-separate border-spacing-0 text-sm">
        <thead className="ui-thead [&_th:first-child]:left-0 [&_th:first-child]:z-20 [&_th:first-child]:border-r">
          <tr>
            {head.map((h, i) => (
              <th
                key={i}
                className={cx(
                  'sticky top-0 z-10 border-b border-rule bg-sheet px-3 py-2 text-xs font-medium whitespace-nowrap text-muted',
                  isAlignedHead(h) ? 'text-right' : 'text-left',
                )}
              >
                {isAlignedHead(h) ? h.label : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="[&_td]:border-b [&_td]:border-rule [&_td]:px-3 [&_td]:py-2 [&_td]:align-middle [&_td:first-child]:sticky [&_td:first-child]:left-0 [&_td:first-child]:z-[1] [&_td:first-child]:border-r [&_td:first-child]:bg-sheet [&_tr:hover_td]:bg-paper [&_tr:last-child_td]:border-b-0 [&_tr[data-total]_td]:bg-paper [&_tr[data-total]_td]:font-semibold">
          {children}
        </tbody>
      </table>
    </div>
  );
}

/** Linha de total ou subtotal de uma `ValueTable`: letra mais forte e fundo levemente destacado. */
export function TotalRow({ children }: { children: ReactNode }) {
  return <tr data-total="">{children}</tr>;
}

/** Célula de valor ou quantidade: à direita, algarismos de mesma largura, sem quebrar linha. */
export function NumCell({ children }: { children: ReactNode }) {
  return <td className="text-right whitespace-nowrap tabular-nums">{children}</td>;
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-8 text-center text-muted">
        {children}
      </td>
    </tr>
  );
}

/** Mensagem do erro da API para um campo (`path` do zod), para mostrar ao lado dele. */
export function issueFor(error: unknown, path: string): string | undefined {
  return error instanceof ApiError
    ? error.issues.find((i) => i.path === path || i.path.startsWith(`${path}.`))?.message
    : undefined;
}

/**
 * A chave do texto de um código de erro da API. Código de um módulo instalado vem com o id dele
 * na frente (`orders.not_draft`) e o texto mora nos textos do módulo (`orders.errors.not_draft`,
 * ADR 0123); os da base ficam em `errors.<código>`.
 */
export function errorTextKey(code: string | null | undefined): string {
  const m = /^([a-z][a-z0-9_]*)\.([a-z_]+)$/.exec(code ?? '');
  return m ? `${m[1]}.errors.${m[2]}` : `errors.${code ?? 'internal_error'}`;
}

/** Mostra um erro da API em linguagem simples, com a lista de campos a corrigir. */
/**
 * Erro devolvido pela API. `showPaths={false}` esconde o nome técnico do campo nas pendências
 * (use quando o formulário já mostra cada erro ao lado do campo). Quando o banco explica o motivo
 * (regra sem campo), a mensagem genérica dá lugar ao motivo.
 */
export function ErrorBox({ error, showPaths = true }: { error: unknown; showPaths?: boolean }) {
  const { t } = useTranslation();
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  const reasonOnly =
    (e?.code === 'invalid_value' || e?.code === 'forbidden_by_database') &&
    e.issues.length > 0 &&
    e.issues.every((i) => !i.path);
  const key = reasonOnly ? 'errors.invalid_value_reason' : errorTextKey(e?.code);
  return (
    <div
      role="alert"
      className="ui-alert rounded-lg p-3 text-sm"
      style={{ '--c': 'var(--danger)' } as CSSProperties}
    >
      <p>{t(key, { ...e?.details, defaultValue: t('errors.internal_error') })}</p>
      {e?.issues && e.issues.length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {e.issues.map((i, idx) => (
            <li key={idx}>
              {showPaths && i.path && <code className="font-mono">{i.path}</code>} {i.message}
            </li>
          ))}
        </ul>
      )}
      {e?.requestId && (
        <p className="mt-1 font-mono text-xs opacity-70">
          {t('errors.reference')}: {e.requestId}
        </p>
      )}
    </div>
  );
}

export function Loading() {
  const { t } = useTranslation();
  return <p className="text-sm text-muted">{t('common.loading')}</p>;
}

export function Textarea(props: ComponentProps<'textarea'>) {
  return <textarea rows={3} {...props} className={cx(inputClass, 'min-h-20', props.className)} />;
}
